<template>
  <section>
    <div class="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
      <div>
        <div class="eyebrow">Cadastros</div>
        <h1 class="mt-2 text-3xl font-extrabold text-[#102a5a]">{{ title }}</h1>
        <p class="mt-1 text-sm text-slate-500">{{ subtitle }}</p>
      </div>
      <button v-if="!editing" class="btn-primary" type="button" @click="openNew"><AppIcon name="plus" class="h-4 w-4" />Incluir novo</button>
    </div>

    <section v-if="editing" class="card overflow-hidden">
      <div class="entity-form-header flex flex-col gap-3 border-b border-stone-200 bg-[#f1ece4] px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex items-center gap-3">
          <span class="icon-box"><AppIcon :name="icon" /></span>
          <div><div class="eyebrow">Dados cadastrais</div><h2 class="mt-1 text-xl font-bold">{{ form.id ? `Alterar ${singular}` : (newLabel || `Novo ${singular}`) }}</h2></div>
        </div>
        <span class="badge-amber">Campos obrigatórios *</span>
      </div>
      <form class="p-6" @submit.prevent="save">
        <div v-if="recordLockField && form[recordLockField]" class="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Esta conta já possui lançamentos. Os campos estruturais identificados abaixo estão bloqueados para preservar o histórico financeiro.
        </div>
        <div class="catalog-form-grid">
          <label v-for="field in fields" :key="field.key" class="catalog-form-field" :style="{ '--field-span': field.span || 4 }">
            <span class="label">{{ field.label }}{{ field.required ? ' *' : '' }}</span>
            <select v-if="field.type === 'select'" v-model="form[field.key]" class="input" :disabled="fieldDisabled(field)" @change="fieldChanged(field)">
              <option v-for="option in field.options" :key="String(option.value)" :value="option.value" :disabled="option.disabled">{{ option.label }}</option>
            </select>
            <input
              v-else
              v-model.trim="form[field.key]"
              class="input"
              :type="field.type === 'email' ? 'email' : 'text'"
              :maxlength="field.maxlength"
              :placeholder="field.placeholder"
              :disabled="fieldDisabled(field)"
              autocomplete="off"
              @input="field.uppercase && uppercaseField(field.key, $event)"
            >
          </label>
        </div>
        <div class="mt-6 flex flex-col-reverse gap-3 border-t border-stone-200 pt-6 sm:flex-row sm:justify-end">
          <button class="btn-secondary" type="button" :disabled="saving" @click="cancelEdit">Cancelar</button>
          <button class="btn-primary" type="submit" :disabled="saving"><AppIcon name="save" class="h-4 w-4" />{{ saving ? 'Salvando...' : `Salvar ${singular}` }}</button>
        </div>
      </form>
    </section>

    <template v-else>
      <section class="card filter-panel entity-filter-panel mb-5">
        <form class="entity-filter-grid" @submit.prevent="applySearch">
          <label class="entity-search-field"><span class="label">Pesquisar registro</span><span class="search-input-wrap"><AppIcon name="search" /><input v-model.trim="search" class="input" type="search" :placeholder="searchPlaceholder"></span></label>
          <button class="btn-primary entity-search-button" type="submit" :disabled="loading"><AppIcon name="search" class="h-4 w-4" />Buscar</button>
          <div class="record-summary" aria-live="polite"><strong>{{ totalCount }}</strong><span>registro{{ totalCount === 1 ? '' : 's' }} encontrado{{ totalCount === 1 ? '' : 's' }}</span></div>
        </form>
      </section>

      <section class="card overflow-hidden">
        <div class="flex flex-col gap-2 border-b border-stone-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div><h2 class="text-lg font-bold">{{ listTitle }}</h2><p class="text-sm text-slate-500">Clique no nome de uma coluna para ordenar os registros.</p></div>
          <span v-if="loading" class="loading-label"><span class="loading-spinner" />Atualizando</span>
        </div>
        <div class="table-scroll overflow-x-auto">
          <table class="tbl entity-table">
            <thead><tr>
              <th v-for="column in columns" :key="column.value" :class="column.className">
                <button v-if="column.sortable !== false" type="button" class="sort-button" @click="sort(column.value)">
                  {{ column.text }} <span class="sort-indicator" :class="{ active: sortBy === column.value }" aria-hidden="true">{{ sortIcon(column.value) }}</span>
                  <span class="sr-only">{{ sortDescription(column.value) }}</span>
                </button>
                <span v-else>{{ column.text }}</span>
              </th>
              <th class="actions-heading">Ações</th>
            </tr></thead>
            <tbody>
              <tr v-if="loading && items.length === 0"><td :colspan="columns.length + 1" class="empty-state">Carregando registros...</td></tr>
              <tr v-else-if="items.length === 0"><td :colspan="columns.length + 1" class="empty-state">Nenhum registro encontrado.</td></tr>
              <tr v-for="item in items" v-else :key="item.id">
                <td v-for="column in columns" :key="column.value" :data-label="column.text">
                  <span v-if="column.type === 'code'" class="code-chip">{{ item[column.value] }}</span>
                  <span v-else-if="column.type === 'status'" :class="item[column.value] ? 'badge-green' : 'badge-red'">{{ item[column.value] ? 'Ativo' : 'Inativo' }}</span>
                  <span v-else-if="column.type === 'boolean'" :class="item[column.value] ? 'badge-blue' : 'badge-amber'">{{ item[column.value] ? 'Sim' : 'Não' }}</span>
                  <span v-else-if="column.type === 'direction'" class="badge-blue">{{ item[column.value] === 'R' ? 'A receber' : item[column.value] }}</span>
                  <span v-else-if="column.type === 'date'">{{ formatDate(item[column.value]) }}</span>
                  <b v-else-if="column.emphasis">{{ item[column.value] }}</b>
                  <span v-else>{{ item[column.value] }}</span>
                </td>
                <td data-label="Ações" class="actions-cell"><span class="row-actions">
                  <button class="row-action edit" type="button" title="Editar" aria-label="Editar" @click="openEdit(item.id)"><AppIcon name="pencil" /></button>
                  <button
                    class="row-action delete"
                    type="button"
                    :title="deleteBlockedField && item[deleteBlockedField] ? deleteBlockedTitle : 'Excluir'"
                    aria-label="Excluir"
                    :disabled="Boolean(deleteBlockedField && item[deleteBlockedField])"
                    @click="remove(item)"
                  ><AppIcon name="trash" /></button>
                </span></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="pagination-footer flex flex-col gap-4 border-t border-stone-200 px-5 py-4 text-sm text-slate-500 lg:flex-row lg:items-center lg:justify-between">
          <span>Exibindo {{ displayStart }} a {{ displayEnd }} de {{ totalCount }}</span>
          <div class="pagination-controls">
            <label>Linhas por página<select v-model.number="pageSize" class="pagination-select" @change="changePageSize"><option v-for="size in pageSizeOptions" :key="size" :value="size">{{ size }}</option></select></label>
            <span>Página {{ totalPages === 0 ? 0 : page }} de {{ totalPages }}</span>
            <div class="pagination-buttons">
              <button type="button" aria-label="Página anterior" :disabled="page <= 1 || loading" @click="goToPage(page - 1)"><AppIcon name="chevronLeft" /></button>
              <button class="current-page" type="button" aria-current="page">{{ totalPages === 0 ? 0 : page }}</button>
              <button type="button" aria-label="Próxima página" :disabled="page >= totalPages || loading" @click="goToPage(page + 1)"><AppIcon name="chevronRight" /></button>
            </div>
          </div>
        </div>
      </section>
    </template>
  </section>
</template>

<script>
import Swal from 'sweetalert2'
import AppIcon from './AppIcon.vue'

export default {
  name: 'CatalogCrudView',
  components: { AppIcon },
  emits: ['field-change', 'record-loaded', 'record-new'],
  props: {
    title: { type: String, required: true },
    subtitle: { type: String, required: true },
    singular: { type: String, required: true },
    listTitle: { type: String, required: true },
    newLabel: { type: String, default: '' },
    icon: { type: String, default: 'tags' },
    searchPlaceholder: { type: String, default: 'Nome ou código' },
    service: { type: Object, required: true },
    fields: { type: Array, required: true },
    columns: { type: Array, required: true },
    formDefaults: { type: Object, default: () => ({}) },
    itemNameField: { type: String, default: 'name' },
    defaultSortBy: { type: String, default: 'name' },
    recordLockField: { type: String, default: '' },
    deleteBlockedField: { type: String, default: '' },
    deleteBlockedTitle: { type: String, default: 'Este registro não pode ser excluído.' }
  },
  data () {
    return {
      items: [],
      totalCount: 0,
      totalPages: 0,
      page: 1,
      pageSize: Number(process.env.VUE_APP_DEFAULT_PAGE_SIZE) || 40,
      pageSizeOptions: [20, 40, 80, 100],
      search: '',
      sortBy: this.defaultSortBy,
      sortDirection: 'asc',
      loading: false,
      saving: false,
      editing: false,
      form: { id: 0, isActive: true, ...this.formDefaults }
    }
  },
  computed: {
    displayStart () { return this.totalCount === 0 ? 0 : ((this.page - 1) * this.pageSize) + 1 },
    displayEnd () { return Math.min(this.page * this.pageSize, this.totalCount) }
  },
  created () { this.load() },
  methods: {
    createEmptyForm () { return { id: 0, isActive: true, ...this.formDefaults } },
    async load () {
      this.loading = true
      try {
        const result = await this.service.getPaged({ page: this.page, pageSize: this.pageSize, search: this.search || undefined, sortBy: this.sortBy, sortDirection: this.sortDirection })
        this.items = result.items
        this.totalCount = result.totalCount
        this.totalPages = result.totalPages
      } catch (error) { await this.showError(error) } finally { this.loading = false }
    },
    openNew () { this.form = this.createEmptyForm(); this.editing = true; this.$emit('record-new', this.form) },
    async openEdit (id) {
      this.loading = true
      try {
        this.form = { ...this.createEmptyForm(), ...await this.service.getById(id) }
        this.editing = true
        this.$emit('record-loaded', this.form)
      } catch (error) { await this.showError(error) } finally { this.loading = false }
    },
    cancelEdit () { this.form = this.createEmptyForm(); this.editing = false },
    uppercaseField (key, event) { this.form[key] = event.target.value.toUpperCase() },
    fieldDisabled (field) { return Boolean(field.disabled || (field.lockWhenUsed && this.recordLockField && this.form[this.recordLockField])) },
    fieldChanged (field) { this.$emit('field-change', { key: field.key, value: this.form[field.key], form: this.form }) },
    async save () {
      const missing = this.fields.some(field => {
        const value = this.form[field.key]
        return field.required && (value === null || value === undefined || String(value).trim() === '' || (field.type === 'select' && Number(value) === 0))
      })
      if (missing) { await Swal.fire('Atenção', 'Informe todos os campos obrigatórios.', 'warning'); return }
      this.saving = true
      try {
        if (this.form.id) await this.service.update(this.form)
        else await this.service.add(this.form)
        await Swal.fire({ icon: 'success', title: 'Registro salvo com sucesso.', timer: 1800, showConfirmButton: false })
        this.cancelEdit()
        await this.load()
      } catch (error) { await this.showError(error) } finally { this.saving = false }
    },
    async remove (item) {
      const confirmation = await Swal.fire({ icon: 'warning', title: `Excluir ${item[this.itemNameField]}?`, text: 'Esta ação não poderá ser desfeita.', showCancelButton: true, confirmButtonText: 'Excluir', cancelButtonText: 'Cancelar', confirmButtonColor: '#e77900', reverseButtons: true })
      if (!confirmation.isConfirmed) return
      try {
        await this.service.remove(item.id)
        if (this.items.length === 1 && this.page > 1) this.page--
        await Swal.fire({ icon: 'success', title: 'Registro excluído.', timer: 1500, showConfirmButton: false })
        await this.load()
      } catch (error) { await this.showError(error) }
    },
    sort (column) {
      if (this.sortBy === column) this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      else { this.sortBy = column; this.sortDirection = 'asc' }
      this.page = 1
      this.load()
    },
    sortIcon (column) { return this.sortBy !== column ? '↕' : (this.sortDirection === 'asc' ? '↑' : '↓') },
    sortDescription (column) { return this.sortBy !== column ? 'Ativar ordenação por esta coluna' : (this.sortDirection === 'asc' ? 'Ordenado de forma crescente' : 'Ordenado de forma decrescente') },
    applySearch () { this.page = 1; this.load() },
    changePageSize () { this.page = 1; this.load() },
    goToPage (page) { if (page < 1 || page > this.totalPages || page === this.page) return; this.page = page; this.load() },
    formatDate (value) { return value ? new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value)).replace(',', '') : '—' },
    async showError (error) {
      const validationErrors = error?.response?.data?.errors
      const firstValidation = validationErrors ? Object.values(validationErrors).flat()[0] : null
      const message = firstValidation || error?.response?.data?.title || error?.response?.data?.detail || 'Não foi possível concluir a operação.'
      await Swal.fire({ title: 'Atenção', text: message, icon: 'error', confirmButtonColor: '#e77900' })
    }
  }
}
</script>
